import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import SideBarDropDown from '../SideBarDropDown/SideBarDropDown';
import useRouteSelected from '../../hooks/useRouteSelected';
import sidebarRoutes from '../../constants/sidebarRoutes';
import useSidebar from '../../hooks/useSidebar';
import useRoles from '../../hooks/useRoles';
import useWhiteLabelSidebar from '../../whiteLabel/useWhiteLabelSidebar';
import chartIcon from '../../assets/sidebar/icons/Chart';
import restartIcon from '../../assets/sidebar/icons/RestartTestgroup';
import homeIcon from '../../assets/sidebar/icons/Home';
import categoriesIcon from '../../assets/sidebar/icons/CategoriesIcon';
import alkyMationIcon from '../../assets/sidebar/icons/AlkyMationSideBar';
import { t } from '../../i18n/i18n';

import './sidebar.scss';
import {
  CATEGORIES_TEST,
  SUBSKILLS_TEST,
  CREATE_ABM_TESTGROUP_SCREENING,
  HOME,
  RESET_TEST_GROUP,
  ROLES_TESTGROUP,
  RoutesKeys,
  SCREENING_SEARCHES,
  BULK_UPDATE_TESTS,
} from '../../constants/routes';
import { Window } from '../../utils/window';
import WorkspaceDropdown from '../WorkspaceDropdown/WorkspaceDropdown';
import DisabledFeature from '../DisabledFeature/DisabledFeature';
import { PlanFeaturesIds } from '../../constants/enums';
import { auth0Client } from '../../auth0/auth0';
import LogoutIcon from '../../assets/sidebar/icons/LogoutIcon';
import { clearLocalStorageOnLogout } from '../../utils/localStorageUtils';

const ShowRouteForWorkspaceRole = ({ roles = [], currentRole, children }) => {
  if (roles.includes(currentRole) || roles.length === 0) {
    return <div className="d-flex align-items-center">{children}</div>;
  }
  return null;
};

const SideBar = () => {
  const { collapsed, setCollapsed } = useSidebar();
  const { selected } = useRouteSelected(sidebarRoutes);
  const { roles } = useRoles();
  const history = useHistory();
  const { routes } = useWhiteLabelSidebar();
  const [createTestGroupCondition, setCreateTestGroupCondition] = useState(true);
  const [resetTestGroupCondition, setResetTestGroupCondition] = useState(true);
  const user = useSelector((state) => state.auth.user);
  const plan = useSelector((state) => state.auth.plan);
  const { currentRole, currentCompany, currentWorkspace } = useSelector((state) => state.auth);

  const onClick = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (Window()) {
      setCreateTestGroupCondition(
        JSON.parse(localStorage.getItem('roles'))?.includes(roles.TESTGROUPCREATOR)
      );
      setResetTestGroupCondition(
        JSON.parse(localStorage.getItem('roles'))?.includes(roles.TESTGROUPRESTARTER)
      );
    }
  }, [user]);

  const filterSubroutes = (subRoutes) => {
    const filteredRoutes = subRoutes.filter((r) => {
      if (r.workspaceRoles) {
        return r.workspaceRoles.includes(currentRole);
      }
      return true;
    });
    return filteredRoutes;
  };

  const flattenRoutes = (routes) => {
    return routes
      .filter((route) => !route.hide && route.key !== RoutesKeys.CONFIGURATION)
      .flatMap((route) => {
        if (route.subRoutes?.length) {
          const filteredSubRoutes = route.subRoutes.filter((sub) => {
            if (sub.label === 'CHAT_DRAFTS_TITLE') {
              const allWorkspacesAllowed = t('ASSISTANT_ALL_WORKSPACES_ALLOWED') === 'true';
              if (!allWorkspacesAllowed) {
                const allowedWorkspaces = t('ASSISTANT_WORKSPACES_ALLOWED')
                  .split(',')
                  .map((id) => id.trim());
                if (!currentWorkspace || !allowedWorkspaces.includes(currentWorkspace.toString())) {
                  return false;
                }
              }
            }
            return true;
          });

          return filteredSubRoutes.map((sub) => ({
            ...sub,
            key: `${route.key}_${sub.label}`,
            icon: sub.icon ?? route.icon,
            workspaceRoles: sub.workspaceRoles || route.workspaceRoles,
            featureId: route.featureId,
          }));
        }
        return [route];
      });
  };

  const logout = () => {
    if (Window()) {
      clearLocalStorageOnLogout();
      auth0Client().logout({ returnTo: window.location.origin });
    }
  };

  return (
    <div
      data-testid="side-bar-component"
      className={`sidebar position-fixed h-100 non-selectable ${collapsed ? 'close-sidebar' : ''}`}
    >
      <div className="my-2 d-flex justify-content-end pr-3 mr-1" aria-hidden="true">
        <div
          type="button"
          data-testid="burger-button-menu"
          className={`nav-links__item burger-menu d-flex align-items-center menu-collapses d-flex justify-content-end unstyled-button p-2 main-nav-toggle ${
            !collapsed ? 'active-menu' : ''
          }`}
          onClick={onClick}
          aria-hidden="true"
        >
          <span className={`main-nav-toggle ${!collapsed ? 'active-menu' : ''}`}>
            <i className={`${!collapsed ? 'd-none' : 'd-block'}`}>-</i>
          </span>
        </div>
      </div>
      <div
        style={{ paddingBottom: '100px' }}
        className={`${!collapsed ? 'sidebar-scrollable' : ''}`}
      >
        <ul className="nav-links">
          <li
            data-testid="li-sidebar-workspace-dropdown"
            aria-hidden="true"
            key={`${RoutesKeys.WORKSPACE}-dropdown`}
            id="DROPDOWN_WORKSPACE"
            className="my-4"
          >
            <WorkspaceDropdown
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              className="pading-left-xs"
              customBackground
            />
          </li>
          <li
            className={`my-2 ${!collapsed && 'overflow-hidden'}`}
            aria-hidden="true"
            key={RoutesKeys.HOME}
          >
            <Link
              className="dropdown-link d-flex align-items-center bg-transparent border-0"
              to={HOME}
            >
              <SideBarDropDown
                icon={homeIcon}
                imgWidth="35"
                imgClassName="ml-2"
                imgPadding="pading-left-xxs"
                collapsed={collapsed}
                selected={history.location.pathname === HOME}
                buttonText="HOME_PAGE_BANNER"
              />
            </Link>
          </li>
          <li
            className={`my-2 ${!collapsed && 'overflow-hidden'}`}
            aria-hidden="true"
            key="ALKYMATION"
            id="ALKYMATION"
          >
            {Number(plan?.tier) === 1 ? (
              <div className="d-flex align-items-center">
                <DisabledFeature
                  className={`d-flex flex-row-reverse ${
                    collapsed ? '' : 'w-100'
                  } gap-0 align-items-center justify-content-between`}
                  childrenClassname="disable-child-side-bar p-0"
                  iconSize="micro"
                  iconClassName="disabled-icon-side-bar"
                  featureId={PlanFeaturesIds.ALKYMATION}
                  validateAmount={false}
                  usedAmount={0}
                  hideDiamond={collapsed}
                >
                  <Link to="/alkymation" className="m-0">
                    <SideBarDropDown
                      icon={alkyMationIcon}
                      imgWidth="35"
                      imgClassName="alkyMationIcon"
                      imgPadding="pading-left-xxs"
                      collapsed={collapsed}
                      selected={history.location.pathname === '/alkymation'}
                      buttonText="AlkyMation"
                      isPremium
                      disabledFeature
                    />
                  </Link>
                </DisabledFeature>
              </div>
            ) : (
              <Link
                className="dropdown-link d-flex align-items-center bg-transparent border-0"
                to="/alkymation"
              >
                <SideBarDropDown
                  icon={alkyMationIcon}
                  imgWidth="35"
                  imgClassName="alkyMationIcon"
                  imgPadding="pading-left-xxs"
                  collapsed={collapsed}
                  selected={history.location.pathname === '/alkymation'}
                  buttonText="AlkyMation"
                />
              </Link>
            )}
          </li>
          {!!routes?.length &&
            flattenRoutes(routes).map((route) => (
              <li
                className={`my-2 ${!collapsed && 'overflow-hidden'}`}
                aria-hidden="true"
                key={route.key}
                data-testid={route.key}
                id={route.label}
              >
                {route?.path ? (
                  <ShowRouteForWorkspaceRole
                    currentRole={currentRole}
                    roles={route?.workspaceRoles}
                  >
                    {[PlanFeaturesIds.MARKETPLACE, PlanFeaturesIds.ALKYMATION].includes(
                      route?.featureId
                    ) && Number(plan?.tier) === 1 ? (
                      <DisabledFeature
                        className={`d-flex flex-row-reverse ${
                          collapsed ? '' : 'w-100'
                        } gap-0 align-items-center`}
                        childrenClassname="disable-child-side-bar"
                        iconSize="micro"
                        iconClassName="disabled-icon-side-bar"
                        featureId={route?.featureId}
                        validateAmount={false}
                        usedAmount={0}
                        hideDiamond={collapsed}
                      >
                        {filterSubroutes(route?.subRoutes ?? []).length > 0 ? (
                          <div
                            className="m-0"
                            onClick={() => {
                              if (!collapsed) {
                                history.push(route.path);
                              }
                            }}
                            style={{ cursor: collapsed ? 'default' : 'pointer' }}
                          >
                            <SideBarDropDown
                              icon={route?.icon}
                              buttonText={route.label}
                              collapsed={collapsed}
                              selected={selected === route.key}
                              imgClassName={`ml-3 pr-1 ${
                                route?.path === SCREENING_SEARCHES && 'pading-left-xxxs'
                              }`}
                              options={filterSubroutes(route?.subRoutes ?? [])}
                              isPremium={!!route?.featureId}
                              disabledFeature
                            />
                          </div>
                        ) : (
                          <Link to={route?.path} className="m-0">
                            <SideBarDropDown
                              icon={route?.icon}
                              buttonText={route.label}
                              collapsed={collapsed}
                              selected={selected === route.key}
                              imgClassName={`ml-3 pr-1 ${
                                route?.path === SCREENING_SEARCHES && 'pading-left-xxxs'
                              }`}
                              options={filterSubroutes(route?.subRoutes ?? [])}
                              isPremium={!!route?.featureId}
                              disabledFeature
                            />
                          </Link>
                        )}
                      </DisabledFeature>
                    ) : filterSubroutes(route?.subRoutes ?? []).length > 0 ? (
                      <div
                        className={`m-0 ${collapsed ? '' : 'w-100'}`}
                        onClick={() => {
                          if (!collapsed) {
                            history.push(route.path);
                          }
                        }}
                        style={{ cursor: collapsed ? 'default' : 'pointer' }}
                      >
                        <SideBarDropDown
                          icon={route?.icon}
                          buttonText={route.label}
                          collapsed={collapsed}
                          selected={history.location.pathname === route?.path}
                          imgClassName={`ml-3 pr-1 ${
                            route?.path === SCREENING_SEARCHES && 'pading-left-xxxs'
                          }`}
                          options={filterSubroutes(route?.subRoutes ?? [])}
                        />
                      </div>
                    ) : (
                      <Link to={route?.path} className={`m-0 ${collapsed ? '' : 'w-100'}`}>
                        <SideBarDropDown
                          icon={route?.icon}
                          buttonText={route.label}
                          collapsed={collapsed}
                          selected={history.location.pathname === route?.path}
                          imgClassName={`ml-3 pr-1 ${
                            route?.path === SCREENING_SEARCHES && 'pading-left-xxxs'
                          }`}
                          options={filterSubroutes(route?.subRoutes ?? [])}
                        />
                      </Link>
                    )}
                  </ShowRouteForWorkspaceRole>
                ) : (
                  <ShowRouteForWorkspaceRole
                    currentRole={currentRole}
                    roles={route?.workspaceRoles}
                    featureId={route?.featureId}
                  >
                    <SideBarDropDown
                      icon={route?.icon}
                      buttonText={route.label}
                      collapsed={collapsed}
                      selected={selected === route.key}
                      imgClassName={
                        route.key === RoutesKeys.SCREENING_INVITATIONS ? 'ml-1_2rem' : 'ml-3'
                      }
                      options={filterSubroutes(route?.subRoutes ?? [])}
                    />
                  </ShowRouteForWorkspaceRole>
                )}
              </li>
            ))}
          {createTestGroupCondition && (
            <li
              className={`my-2 ${!collapsed && 'overflow-hidden'}`}
              aria-hidden="true"
              key={RoutesKeys.CREATE_ABM_TESTGROUP_SCREENING}
              id={RoutesKeys.CREATE_ABM_TESTGROUP_SCREENING}
            >
              <Link
                className="dropdown-link d-flex align-items-center bg-transparent border-0"
                to={CREATE_ABM_TESTGROUP_SCREENING}
              >
                <SideBarDropDown
                  icon={chartIcon}
                  imgClassName="ml-3"
                  collapsed={collapsed}
                  selected={history.location.pathname === CREATE_ABM_TESTGROUP_SCREENING}
                  buttonText="MENU_ITEM_CREATE_TEST_GROUP"
                />
              </Link>
            </li>
          )}
          {createTestGroupCondition && (
            <li
              className={`my-2 ${!collapsed && 'overflow-hidden'}`}
              aria-hidden="true"
              key={RoutesKeys.BULK_UPDATE_TESTS}
              id={RoutesKeys.BULK_UPDATE_TESTS}
            >
              <Link
                className="dropdown-link d-flex align-items-center bg-transparent border-0"
                to={BULK_UPDATE_TESTS}
              >
                <SideBarDropDown
                  icon={chartIcon}
                  imgClassName="ml-3"
                  collapsed={collapsed}
                  selected={history.location.pathname === BULK_UPDATE_TESTS}
                  buttonText="MENU_ITEM_BULK_TEST"
                />
              </Link>
            </li>
          )}
          {createTestGroupCondition && (
            <li
              className={`my-2 ${!collapsed && 'overflow-hidden'}`}
              aria-hidden="true"
              key={RoutesKeys.CREATE_CATEGORIES}
              id={RoutesKeys.CREATE_CATEGORIES}
            >
              <Link
                className="dropdown-link d-flex align-items-center bg-transparent border-0"
                to={CATEGORIES_TEST}
              >
                <SideBarDropDown
                  icon={categoriesIcon}
                  imgClassName="ml-3"
                  collapsed={collapsed}
                  selected={history.location.pathname === CATEGORIES_TEST}
                  buttonText="CATEGORIES"
                />
              </Link>
            </li>
          )}
          {createTestGroupCondition && (
            <li
              className={`my-2 ${!collapsed && 'overflow-hidden'}`}
              aria-hidden="true"
              key={RoutesKeys.CREATE_SUBSKILLS}
              id={RoutesKeys.CREATE_SUBSKILLS}
            >
              <Link
                className="dropdown-link d-flex align-items-center bg-transparent border-0"
                to={SUBSKILLS_TEST}
              >
                <SideBarDropDown
                  icon={categoriesIcon}
                  imgClassName="ml-3"
                  collapsed={collapsed}
                  selected={history.location.pathname === SUBSKILLS_TEST}
                  buttonText="SUBSKILLS"
                />
              </Link>
            </li>
          )}
          {createTestGroupCondition && (
            <li
              className={`my-2 ${!collapsed && 'overflow-hidden'}`}
              aria-hidden="true"
              key={RoutesKeys.CREATE_ROLES}
              id={RoutesKeys.CREATE_ROLES}
            >
              <Link
                className="dropdown-link d-flex align-items-center bg-transparent border-0"
                to={ROLES_TESTGROUP}
              >
                <SideBarDropDown
                  icon={categoriesIcon}
                  imgClassName="ml-3"
                  collapsed={collapsed}
                  selected={history.location.pathname === ROLES_TESTGROUP}
                  buttonText="Roles"
                />
              </Link>
            </li>
          )}
          {resetTestGroupCondition && (
            <li
              className={`my-2 ${!collapsed && 'overflow-hidden'}`}
              aria-hidden="true"
              key={RoutesKeys.RESET_TEST_GROUP}
              id={RoutesKeys.RESET_TEST_GROUP}
            >
              <Link
                className="dropdown-link d-flex align-items-center bg-transparent border-0"
                to={RESET_TEST_GROUP}
              >
                <SideBarDropDown
                  icon={restartIcon}
                  imgClassName="ml-3"
                  collapsed={collapsed}
                  selected={history.location.pathname === RESET_TEST_GROUP}
                  buttonText="MENU_ITEM_RESET_TEST_GROUP"
                />
              </Link>
            </li>
          )}
          <li
            className={`my-2 ${!collapsed && 'overflow-hidden'}`}
            aria-hidden="true"
            id={RoutesKeys.LOGOUT}
          >
            <div
              className="dropdown-link d-flex align-items-center bg-transparent border-0"
              onClick={logout}
            >
              <SideBarDropDown
                icon={LogoutIcon}
                imgClassName="ml-3"
                collapsed={collapsed}
                buttonText="BUTTON_LOGOUT"
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SideBar;
